import styled from "@emotion/styled";
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Checkbox,
  ListItemText
} from "@mui/material";
import { usePermissions } from "../../../hooks/usePermissions";
import { LoadingScreen } from "../../shared/LoadingScreen";
import { PermissionDenied } from "../../shared/PermissionDenied";
import { useMutation, useQuery, keepPreviousData } from "@tanstack/react-query";
import { useState, useEffect, useRef, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import Cookies from "universal-cookie";
import {
  deleteApiServices,
  deletewithQueryApiServices,
  getApiServices,
  getWithQueryApiServices,
  putWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { companyFormInitVal } from "../../../constants";
import { SplitContainer, StyledHeader } from "../../../styles/commonStyles";
import { successToast, errorToast } from "../../../utils/helper";
import { FullFeaturedCrudGrid } from "../../shared/CommonTable";
import { ParentPageWrapper } from "../../shared/ParentPageWrapper";
import { CompanyUsers } from "./CompanyUsers";
import { useTenantGuard } from "../../../hooks/useTenantGuard";

const BoxContainer = styled(Box)(({ theme }) => ({
  padding: "10px",
  [theme.breakpoints.up("sm")]: {
    padding: "24px",
  },
  [theme.breakpoints.up("md")]: {
    padding: "1%",
  },
  boxSizing: "border-box",
}));

const ParentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  flexWrap: "wrap",
  gap: "20px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "30px",
  },
}));

const ResponsiveSplitContainer = styled(SplitContainer)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    width: "100% !important",
  },
}));

const ActionContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  boxSizing: "border-box",
  marginLeft: "24px",
}));

const ActionItemContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "36%",
  marginRight: 18,
}));

const GridSelectCell = ({ params, value, onChange, options, disabled, displayEmpty, MenuProps, sx, children }) => {
  const [val, setVal] = useState(value);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setVal(value);
  }, [value]);

  useEffect(() => {
    if (params.hasFocus && containerRef.current) {
      const timer = setTimeout(() => {
        const selectButton = containerRef.current?.querySelector('[role="combobox"]');
        if (selectButton) {
          selectButton.focus();
        } else if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [params.hasFocus]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e) => {
      if (e.key === "Tab") {
        e.stopPropagation();
      } else if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.stopPropagation();
        e.preventDefault();
        const char = e.key.toLowerCase();
        const match = options.find((opt) =>
          opt.label.toLowerCase().split(/\s+/).some((word) => word.startsWith(char))
        );
        if (match !== undefined) {
          setVal(match.value);
          onChange(match.value);
        }
      } else if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "Enter" ||
        e.key === "Escape"
      ) {
        e.stopPropagation();
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, [options, params, onChange]);

  return (
    <Box ref={containerRef} sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", px: 1 }}>
      <FormControl fullWidth size="small">
        <Select
          inputRef={inputRef}
          value={val}
          onChange={(e) => {
            setVal(e.target.value);
            onChange(e.target.value);
          }}
          disabled={disabled}
          displayEmpty={displayEmpty}
          MenuProps={MenuProps}
          sx={sx}
        >
          {children}
        </Select>
      </FormControl>
    </Box>
  );
};

const CompanySecurityEditCell = ({ params, securityProfilesData }) => {
  const [value, setValue] = useState(
    params.value !== undefined && params.value !== null && params.value !== ""
      ? Number(params.value)
      : ""
  );

  const options = useMemo(() => [
    { label: "None", value: "" },
    ...(securityProfilesData?.data?.list?.map((p) => ({
      label: p.name,
      value: Number(p.securityprofileID),
    })) || []),
  ], [securityProfilesData]);

  useEffect(() => {
    setValue(
      params.value !== undefined && params.value !== null && params.value !== ""
        ? Number(params.value)
        : ""
    );
  }, [params.value]);

  return (
    <GridSelectCell
      params={params}
      value={value}
      onChange={(val) => {
        const parsedVal = val !== "" ? Number(val) : "";
        setValue(parsedVal);
        params.api.setEditCellValue({ id: params.id, field: params.field, value: parsedVal });
      }}
      options={options}
      MenuProps={{
        PaperProps: {
          style: {
            maxWidth: 250,
          },
        },
      }}
      sx={{
        "& .MuiSelect-select": {
          overflow: "hidden",
          textOverflow: "ellipsis",
        }
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value} sx={{ maxWidth: "100%" }}>
          <Typography noWrap variant="body2" title={opt.label} sx={{ width: "100%" }}>
            {opt.label}
          </Typography>
        </MenuItem>
      ))}
    </GridSelectCell>
  );
};

export const Index = () => {
  const cookies = new Cookies();
  const defaultcompany = cookies.get("defaultcompany");
  const { globalCompanyRefetch, tenantString, currentTenant, isCompanySwitching } = useOutletContext();

  const [searchCompany, setSearchCompany] = useState("");
  const [debouncedSearchCompany, setDebouncedSearchCompany] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchCompany(searchCompany);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchCompany]);

  const [companyForm, setCompanyForm] = useState(companyFormInitVal);
  const [addNewCompany, setAddNewCompany] = useState(false);
  const [outerValue, setOuterValue] = useState(null);
  const [pendingSelectCompanyId, setPendingSelectCompanyId] = useState(null);
  const [selectedCopyTargetIds, setSelectedCopyTargetIds] = useState([]);
  const [activeDialog, setActiveDialog] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // ✅ Strict Granular Permissions
  const { hasPermission, isGlobalSuperAdmin, isTenantAdmin, isLoading: permissionsLoading } = usePermissions();
  const canReadComp = isGlobalSuperAdmin || hasPermission("Companies: Read");
  const canCreateComp = isTenantAdmin;
  const canUpdateComp = isGlobalSuperAdmin || hasPermission("Companies: Update");
  const canDeleteComp = isGlobalSuperAdmin || hasPermission("Companies: Delete");
  
  // Backward compatibility for simple checks
  const canManageCompanies = canUpdateComp || canCreateComp || canDeleteComp;

  const { isTenantBlocked, tenantMessage } = useTenantGuard();

  // Fetch security profiles
  const { data: securityProfilesData, isLoading: isProfilesLoading } = useQuery({
    queryKey: ["securityProfiles", currentTenant],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_SECURITY_PROFILE_LIST,
        `selectedTenant=${currentTenant}`
      ),
  });

  const columns = [
    {
      field: "name",
      headerName: "Company Name",
      flex: 2.0,
      editable: !isTenantBlocked,
      required: true,
    },
    {
      field: "securityprofileID",
      headerName: "Default Security Profile",
      flex: 0.8,
      editable: true,
      renderCell: (params) => {
        return (
          <Box
            sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", px: 1 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <FormControl fullWidth size="small">
              <Select
                value={
                  updateCompanyDetails.isPending && updateCompanyDetails.variables?.companyID === params.row.companyID
                    ? "LOADING"
                    : (params.value !== undefined && params.value !== null && params.value !== "" ? Number(params.value) : "")
                }
                onChange={(e) => {
                  if (isTenantBlocked) {
                    errorToast(tenantMessage);
                    return;
                  }
                  
                  const val = e.target.value !== "" ? Number(e.target.value) : null;
                  // Only trigger update if the company already exists
                  if (!params.row.isNew && params.row.companyID) {
                    params.api.updateRows([{ id: params.id, securityprofileID: val }]);
                    updateCompanyDetails.mutate({
                      ...params.row,
                      securityprofileID: val,
                    });
                  } else {
                    // For new companies, update the edit state
                    params.api.setEditCellValue({ id: params.id, field: params.field, value: val });
                  }
                }}
                disabled={isTenantBlocked || (!canUpdateComp && !isGlobalSuperAdmin) || (updateCompanyDetails.isPending && updateCompanyDetails.variables?.companyID === params.row.companyID)}
                displayEmpty
                IconComponent={updateCompanyDetails.isPending && updateCompanyDetails.variables?.companyID === params.row.companyID 
                  ? () => <CircularProgress size={16} sx={{ mr: 2 }} /> 
                  : undefined
                }
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxWidth: 250,
                    },
                  },
                }}
                sx={{
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }
                }}
              >
                <MenuItem value="LOADING" disabled sx={{ display: 'none' }}>Saving...</MenuItem>
                <MenuItem value="">None</MenuItem>
                {securityProfilesData?.data?.list?.map((profile) => (
                  <MenuItem key={profile.securityprofileID} value={Number(profile.securityprofileID)} sx={{ maxWidth: "100%" }}>
                    <Typography noWrap variant="body2" title={profile.name} sx={{ width: "100%" }}>
                      {profile.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      },
      renderEditCell: (params) => (
        <CompanySecurityEditCell params={params} securityProfilesData={securityProfilesData} />
      ),
    },
  ];

  const handleCompanyCheckbox = (val) => {
    setCompanyForm({ ...val });
    setSelectedCopyTargetIds([]);
    if (!isTenantBlocked) {
      setAddNewCompany(val?.companyID && !val?.isNew ? true : false);
    }
  };

  const handleCancelEdit = () => {
    setCompanyForm(companyFormInitVal);
    setSelectedCopyTargetIds([]);
    setAddNewCompany(false);
  };

  const { data, isLoading: isCompanyLoading, refetch: companyListRefetch, isFetching: isCompanyFetching } = useQuery({
    queryKey: ["companyLists", debouncedSearchCompany, tenantString, paginationModel],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_COMPANY_LIST,
        `pageNumber=${paginationModel.page + 1}&pageSize=${paginationModel.pageSize}&search=${debouncedSearchCompany}${tenantString}`
      ),
    placeholderData: keepPreviousData,
  });

  // Reset page to 0 when search filter changes
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchCompany]);

  useEffect(() => {
    if (pendingSelectCompanyId && data?.data?.list) {
      const newComp = data.data.list.find(
        (comp) => Number(comp.companyID) === Number(pendingSelectCompanyId)
      );
      if (newComp) {
        setCompanyForm({ ...newComp });
        if (!isTenantBlocked) {
          setAddNewCompany(true);
        }
        setPendingSelectCompanyId(null);
      }
    }
  }, [data, pendingSelectCompanyId, isTenantBlocked]);

  const handleGridSuccess = (dataOrRow) => {
    const insertId = dataOrRow?.insertId || dataOrRow?.data?.insertId;
    if (insertId) {
      setPendingSelectCompanyId(insertId);
    } else if (dataOrRow) {
      setCompanyForm({ ...dataOrRow });
    }
    globalCompanyRefetch && globalCompanyRefetch();
  };

  const updateCompanyDetails = useMutation({
    mutationKey: ["updateCompanyDetails"],
    mutationFn: (company) =>
      putWithQueryApiServices(
        apiRoutes.UPDATE_COMPANY,
        `selectedCompany=${defaultcompany}${tenantString}`,
        company
      ),
    onSuccess: (res, variables) => {
      companyListRefetch();
      handleGridSuccess(variables);
      successToast("Company Updated Successfully");
    },
    onError: (error) => {
      if (error?.response?.data?.code !== 'TENANT_NOT_ACTIVE') {
        errorToast(error.response?.data?.message || "Failed to update company");
      }
    },
  });

  const deleteCompany = useMutation({
    mutationKey: ["deleteCompany"],
    mutationFn: (companyId) =>
      deletewithQueryApiServices(
        apiRoutes.DELETE_COMPANY,
        `selectedCompany=${defaultcompany}${tenantString}`,
        companyId
      ),
    onSuccess: () => {
      companyListRefetch();
      globalCompanyRefetch && globalCompanyRefetch();
      handleCancelEdit();
      successToast("Company set to inactive / deleted");
    },
    onError: (error) => {
      if (error?.response?.data?.code !== 'TENANT_NOT_ACTIVE') {
        errorToast(error.response?.data?.message || "Failed to set company to inactive");
      }
    },
  });

  const handleCopySettings = async () => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }
    if (!companyForm?.companyID) {
      errorToast("Please select a current company first");
      return;
    }
    if (!selectedCopyTargetIds || selectedCopyTargetIds.length === 0) {
      errorToast("Please select one or more companies to copy settings to");
      return;
    }

    try {
      for (const targetId of selectedCopyTargetIds) {
        const targetCompany = data?.data?.list?.find(
          (c) => Number(c.companyID) === Number(targetId)
        );
        if (targetCompany) {
          const updatedCompany = {
            ...targetCompany,
            securityprofileID: companyForm.securityprofileID !== undefined ? companyForm.securityprofileID : null,
          };
          await updateCompanyDetails.mutateAsync(updatedCompany);
        }
      }
      setSelectedCopyTargetIds([]);
      successToast("Settings copied successfully to selected companies");
    } catch (err) {
      console.error("Failed to copy settings:", err);
    }
  };

  const handleRenameCompany = () => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }
    if (!companyForm?.companyID) {
      errorToast("Please select a company first");
      return;
    }
    setRenameValue(companyForm?.name || "");
    setActiveDialog("rename");
  };

  const confirmRenameCompany = () => {
    if (renameValue.trim() === "") {
      errorToast("Company name cannot be empty");
      return;
    }
    const updatedCompany = {
      ...companyForm,
      name: renameValue.trim()
    };
    setCompanyForm(updatedCompany);
    updateCompanyDetails.mutate(updatedCompany);
    setActiveDialog(null);
  };

  const handleDeleteCompany = () => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }
    if (!companyForm?.companyID) {
      errorToast("Please select a company first");
      return;
    }
    setActiveDialog("inactive");
  };

  const confirmDeleteCompany = () => {
    updateCompanyDetails.mutate({ ...companyForm, dst: 0 });
    setActiveDialog(null);
  };

  return (
    <ParentPageWrapper>
      <BoxContainer>
        <StyledHeader>Companies</StyledHeader>

        {isTenantBlocked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {tenantMessage}
          </Alert>
        )}

        <ParentContainer>
          {(isCompanyLoading || isProfilesLoading || permissionsLoading) && !isCompanySwitching ? (
            <LoadingScreen message="Loading companies..." />
          ) : !isGlobalSuperAdmin && !canReadComp ? (
            <PermissionDenied message="You do not have permission to view the company list. Please contact your administrator." />
          ) : (
            <>              
            <ResponsiveSplitContainer
                style={{ width: "60%", backgroundColor: "transparent" }}
              >
                <Box sx={{ height: { xs: "80vh", md: "73.6vh" } }}>
                  <div
                    style={{
                      backgroundColor: "white",
                      height: "100%",
                      borderRadius: "10px",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      id="tour-companies-table"
                      style={{
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                      }}
                    >
                      <FullFeaturedCrudGrid
                        initialRows={data?.data?.list}
                        columns={columns}
                        label="company"
                        handleSelection={handleCompanyCheckbox}
                        search={searchCompany}
                        setSearch={setSearchCompany}
                        apiUrl={apiRoutes.UPDATE_COMPANY}
                        refetch={companyListRefetch}
                        initialFormValue={companyFormInitVal}
                        outerValue={outerValue}
                        setOuterValue={setOuterValue}
                        isTenantBlocked={isTenantBlocked}
                        tenantMessage={tenantMessage}
                        onSuccess={handleGridSuccess}
                        onCancelEdit={handleCancelEdit}
                        selectedRowId={companyForm?.companyID || null}
                        canCreate={canCreateComp}
                        canUpdate={canUpdateComp}
                        canDelete={false}
                        extraActionsRenderer={(params) => (
                          <Button
                            variant="text"
                            color={params.row.dst === 0 ? "primary" : "error"}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (params.row.dst === 0) {
                                updateCompanyDetails.mutate({ ...params.row, dst: 1 });
                              } else {
                                setCompanyForm(params.row);
                                setActiveDialog("inactive");
                              }
                            }}
                            sx={{ fontSize: "12px", textTransform: "none", py: 0, fontWeight: 600, width: "100%", display: "flex", justifyContent: "center" }}
                          >
                            {params.row.dst === 0 ? "Set Active" : "Set Inactive"}
                          </Button>
                        )}
                        actionColumnWidth={200}
                        permission={isGlobalSuperAdmin} // Legacy fallback
                        disabled={!canReadComp}
                        hideFooterPagination={true}
                        rowCount={data?.data?.count || 0}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        loading={isCompanyFetching}
                      />
                    </div>
                  </div>
                </Box>
              </ResponsiveSplitContainer>
 
              <ResponsiveSplitContainer
                style={{
                  cursor: addNewCompany ? "auto" : "not-allowed",
                  backgroundColor: "transparent",
                  width: "35%",
                }}
                sx={{
                  height: { xs: "auto", md: "73.6vh" },
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    filter: !addNewCompany ? "blur(5px)" : "none",
                    transition: "filter 0.3s ease",
                    opacity: !addNewCompany ? 0.6 : 1,
                    pointerEvents: !addNewCompany ? "none" : "auto",
                  }}
                >
                  {/* SECTION 1: COPY SETTINGS & FUNCTIONS */}
                  <Box
                    sx={{
                      backgroundColor: "white",
                      boxSizing: "border-box",
                      borderRadius: "10px",
                      p: 2.5,
                      boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                      border: "1px solid #e0e0e0",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2
                    }}
                  >
                    {/* COPY SETTINGS */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
                        Copy Settings
                      </Typography>
                      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                        <Select
                          multiple
                          value={selectedCopyTargetIds}
                          onChange={(e) => setSelectedCopyTargetIds(e.target.value)}
                          displayEmpty
                          renderValue={(selected) => {
                            if (selected.length === 0) return <em>Select Companies</em>;
                            const names = selected.map(id => data?.data?.list?.find(c => c.companyID === id)?.name || id);
                            return names.join(', ');
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxWidth: 250,
                              },
                            },
                          }}
                          sx={{
                            "& .MuiSelect-select": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }
                          }}
                        >
                          <MenuItem value="" disabled sx={{ display: 'none' }}>Select Companies</MenuItem>
                          {data?.data?.list
                            ?.filter(c => c.companyID !== companyForm?.companyID)
                            ?.map(c => (
                              <MenuItem key={c.companyID} value={c.companyID} sx={{ maxWidth: "100%" }}>
                                <Checkbox checked={selectedCopyTargetIds.indexOf(c.companyID) > -1} size="small" sx={{ p: 0.5, mr: 1 }} />
                                <ListItemText primary={c.name} primaryTypographyProps={{ variant: "body2", noWrap: true }} />
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        disabled={selectedCopyTargetIds.length === 0}
                        onClick={handleCopySettings}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Copy Settings
                      </Button>
                    </Box>
                  </Box>

                  {/* SECTION 2: ADD USERS TO COMPANY */}
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                      border: "1px solid #e0e0e0",
                      borderRadius: "10px",
                      overflow: "hidden",
                    }}
                  >
                    <CompanyUsers
                      companyForm={companyForm}
                      addNewCompany={addNewCompany}
                      isTenantBlocked={isTenantBlocked}
                      tenantMessage={tenantMessage}
                    />
                  </Box>
                </Box>
              </ResponsiveSplitContainer>
            </>
          )}
        </ParentContainer>
        <Dialog open={activeDialog === "rename"} onClose={() => setActiveDialog(null)} fullWidth maxWidth="xs">
          <DialogTitle>Rename Company</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Company Name"
              type="text"
              fullWidth
              variant="outlined"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              size="small"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setActiveDialog(null)} variant="outlined" size="small">
              Cancel
            </Button>
            <Button onClick={confirmRenameCompany} variant="contained" size="small" disabled={updateCompanyDetails.isPending}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === "inactive"} onClose={() => setActiveDialog(null)}>
          <DialogTitle>Set Company to Inactive</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to set <strong>{companyForm?.name}</strong> to inactive?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setActiveDialog(null)} variant="outlined" size="small">
              Cancel
            </Button>
            <Button onClick={confirmDeleteCompany} color="error" variant="contained" size="small" disabled={updateCompanyDetails.isPending}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </BoxContainer>
    </ParentPageWrapper>
  );
};